import i18n, { i18n as I18nInstance, InitOptions } from 'i18next';
import intervalPlural from 'i18next-intervalplural-postprocessor';
import CONFIG from '../../config';

import az from '../../locales/az.json';
import ru from '../../locales/ru.json';
import { registerI18nFormatters } from './registerI18nFormatters';

type Locale = 'ru' | 'az';

// Cache for i18n instances to avoid recreating them for each request
// This improves performance while still preventing race conditions
// Each language gets its own instance that can be safely reused
const i18nInstancesCache: Record<Locale, I18nInstance | undefined> = {
  ru: undefined,
  az: undefined
};

/**
 * Creates a thread-safe i18n instance with a specific language
 * This prevents race conditions when multiple requests use the singleton i18n instance
 *
 * CRITICAL: This function creates separate i18n instances per language to avoid
 * race conditions in production with high concurrent load. Each request gets
 * its own instance, preventing one request from affecting another.
 */
export const getI18nForLang = (lang?: unknown): I18nInstance => {
  // Normalize language code: handle variants like 'ru-RU', 'az-AZ', 'RU', 'AZ'
  // Extract base language code (e.g., 'ru' from 'ru-RU' or 'RU')
  let normalizedLang: Locale = CONFIG.defaultLocale as Locale;

  if (typeof lang === 'string') {
    const trimmedLang = lang.trim();
    if (trimmedLang) {
      // Convert to lowercase and extract base language (before hyphen or underscore)
      const baseLang = trimmedLang.toLowerCase().split('-')[0].split('_')[0];
      if (CONFIG.languages.includes(baseLang)) {
        normalizedLang = baseLang as Locale;
      }
    }
  }

  // Return cached instance if available (thread-safe per language)
  // Each language has its own instance, so no race conditions
  const cachedInstance = i18nInstancesCache[normalizedLang];
  if (cachedInstance) {
    return cachedInstance;
  }

  // Create a new i18n instance to avoid race conditions with singleton
  const i18nInstance = i18n.createInstance();

  // Only use intervalPlural plugin - no LanguageDetector needed
  // since we explicitly set the language via `lng` parameter
  i18nInstance.use(intervalPlural);

  // Initialize with the specific language
  // With inline resources (no backend), init is synchronous
  i18nInstance.init({
    lng: normalizedLang,
    fallbackLng: CONFIG.defaultLocale,
    supportedLngs: CONFIG.languages,
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    debug: false,
    resources: {
      az: {
        translation: az
      },
      ru: {
        translation: ru
      }
    },
    interpolation: {
      escapeValue: false
    },
    react: {
      useSuspense: false
    }
  } as InitOptions);

  registerI18nFormatters(i18nInstance);

  // Cache the instance for this language
  i18nInstancesCache[normalizedLang] = i18nInstance;

  return i18nInstance;
};
